/* eslint require-jsdoc: 'error', valid-jsdoc: ["error", { "requireReturn": false }] */
import {
  COMMA,
  pluck,
  preDefStr,
  attrTypeNum,
  pluckNumber,
  HUNDREDSTRING,
  setAttribDefs
} from '@fusioncharts/core/src/lib';
import extent from '@fusioncharts/utils/src/array/extent';
import MSColumn2D from '@fusioncharts/charts/src/chart/mscolumn2d';
import { scrollTo, setAxisScale, resetViewPortConfig, configurer }
  from '@fusioncharts/charts/src/chart/_internal/utils/scroll-apis';
import BoxAndWhiskerDataset from '../../dataset/boxandwhisker';
import datasetFactory from '../../factories/boxandwhisker-dataset';
import BoxAndWhiskerMultiSeriesGroup from '../../dataset/groups/boxandwhisker-multiseries';

setAttribDefs && setAttribDefs({
  whiskerslimitswidthratio: {
    type: attrTypeNum,
    pAttr: 'whiskerslimitswidthratio'
  },
  outliersupperrangeratio: {
    type: attrTypeNum,
    pAttr: 'outliersupperrangeratio'
  },
  outlierslowerrangeratio: {
    type: attrTypeNum,
    pAttr: 'outlierslowerrangeratio'
  },
  showalloutliers: {
    type: attrTypeNum,
    pAttr: 'showalloutliers'
  },
  showmean: {
    type: attrTypeNum,
    pAttr: 'showmean'
  },
  showsd: {
    type: attrTypeNum,
    pAttr: 'showsd'
  },
  showmd: {
    type: attrTypeNum,
    pAttr: 'showmd'
  },
  showqd: {
    type: attrTypeNum,
    pAttr: 'showqd'
  },
  showminvalues: {
    type: attrTypeNum,
    pAttr: 'showminvalues'
  },
  showmaxvalues: {
    type: attrTypeNum,
    pAttr: 'showmaxvalues'
  },
  showq1values: {
    type: attrTypeNum,
    pAttr: 'showq1values'
  },
  showq3values: {
    type: attrTypeNum,
    pAttr: 'showq3values'
  },
  showmedianvalues: {
    type: attrTypeNum,
    pAttr: 'showmedianvalues'
  }
});
let UNDEF,
  POLYGON = preDefStr.POLYGON,
  COLOR_000000 = preDefStr.colors.c000000,
  BOXANDWHISKER_2D = 'boxandwhisker2d',
  ABOVE = 'above',
  COLON_STR = ': ';
/**
 * class for Marimekko chartAPI
 */
class BoxAndWhisker2D extends MSColumn2D {
  /**
   * Provides the name of the chart extension
   *
   * @static
   * @return {string} The name of the chart extension
   */
  static getName () {
    return 'BoxAndWhisker2D';
  }
  /**
   * Provides the name of the chart
   * @return {string} The name of the chart extension
   */
  getName () {
    return 'BoxAndWhisker2D';
  }

  /**
   * Function to state if inputOptions are to be used for this charts
   * @return {Array} value
   */
  static includeInputOptions () {
    return ['SwipeGesture'];
  }

  /**
   * Constructor funtion for Marimekko chartAPI
   */
  constructor () {
    super();
    let iapi = this;
    iapi.maxColWidth = +Infinity;
    iapi.useErrorAnimation = 1;
    iapi.avoidCrispError = 0;
    iapi.plotSpacePercent = 20;
    iapi.useplotgradientcolor = 1;
    iapi.fireGroupEvent = true;
    iapi.hasScroll = true;
    iapi.eiMethods.scrollTo = scrollTo;
    this.registerFactory('dataset', datasetFactory, ['vCanvas']);
  }

  /**
   * Set default chart configuration
   */
  __setDefaultConfig () {
    super.__setDefaultConfig();
    let config = this.config;
    config.avgScrollPointWidth = 40;
    config.decimals = 2;
    config.defaultDatasetType = BOXANDWHISKER_2D;
    config.showplotborder = 1;
    config.plotborderdashlen = 5;
    config.plotborderdashgap = 4;
    config.plotfillalpha = HUNDREDSTRING;
    config.useroundedges = 0;
    config.plotborderthickness = 1;
    config.showvalues = 1;
    config.valuepadding = 2;
    config.decimals = 2;
    config.showtooltip = 1;
    config.maxcolwidth = 50;
    config.rotatevalues = 0;
    config.use3dlighting = 1;
    config.whiskerslimitswidthratio = 40;
    config.outliersupperrangeratio = 0;
    config.outlierslowerrangeratio = 0;
    config.showalloutliers = 0;
    config.showmean = 0;
    config.showsd = 0;
    config.showmd = 0;
    config.showqd = 0;
    config.showminvalues = 1;
    config.showmaxvalues = 1;
    config.showq1values = 0;
    config.showq3values = 0;
    config.showmedianvalues = 1;
    config.tooltipsepchar = COLON_STR;

    // default attributes of line
    config.anchorborderthickness = 1;
    config.anchorimageurl = UNDEF;
    config.anchorimagepadding = 1;
    config.anchorsides = 1;
    config.anchoralpha = UNDEF;
    config.anchorbgalpha = HUNDREDSTRING;
    config.anchorimagealpha = HUNDREDSTRING;
    config.anchorimagescale = 100;
    config.anchorstartangle = 90;
    config.anchorshadow = 0;
    config.anchorbgcolor = UNDEF;
    config.anchorbordercolor = UNDEF;
    config.anchorradius = 3;
    config.linedashlen = 5;
    config.linedashgap = 4;
    config.linedashed = UNDEF;
    config.linealpha = HUNDREDSTRING;
    config.linethickness = 2;
    config.drawfullareaborder = 1;
    config.connectnulldata = 0;
  }

  /**
   * function to create component and configure and also configure the chart
   * It creates components which are depend on data and configure them and also configure chart
   * It calls helper function namely, configure
   * @param  {Object} dataObj contains json data of the chart
   */
  configureAttributes (dataObj) {
    super.configureAttributes(dataObj);
    configurer.call(this, dataObj);
  }

  /**
   * Sets xAxis scale visible region
   */
  _setAxisScale () {
    setAxisScale.call(this, BOXANDWHISKER_2D);
  }

  /**
   * Reset view port
   */
  _resetViewPortConfig () {
    resetViewPortConfig.call(this);
  }

  /**
   * Function to return the custom json data for the mean, meadian, etc (line dataset) of boxAndWhisker
   * @return {Object} The custom JSON Data
   */
  _getLineJSONdata () {
    var iapi = this,
      bwCalc = iapi.getChildren('BoxAndWhiskerStatisticalCalc')[0],
      jsonData = iapi.getFromEnv('dataSource'),
      chartAttrs = jsonData.chart,
      datasets = jsonData.dataset,
      len = datasets.length, // eslint-disable-line good-practices/no-single-usage-variable
      showMean,
      dataset,
      data,
      dataLen,
      setData,
      derivedDS = [],
      currDerivedDS,
      requiredSubDs = [],
      derivedDataValue = [],
      currDerivedDataVal,
      subDSLen,
      currSubDS,
      showSD,
      showQD,
      showMD,
      outliers,
      outliersArr,
      valueMin,
      valueMax,
      value,
      meanValue,
      sdValue,
      mdValue,
      qdValue,
      fn = function (val) {
        value = Number(val);

        [valueMin, valueMax] = bwCalc.values ? extent(bwCalc.values) : [setData.min, setData.max];
        (value < valueMin || value > valueMax) && outliersArr.push({
          showvalue: setData.showoutliervalue,
          valueposition: setData.outliervalueposition,
          value: value,
          index: j,
          anchorbgcolor: setData.outliericoncolor,
          anchorbordercolor: setData.outliericonbordercolor,
          anchorradius: setData.outliericonradius,
          anchoralpha: setData.outliericonalpha,
          anchorbgalpha: setData.outliericonalpha,
          anchorsides: setData.outliericonsides,
          dip: setData.outliericonshape
        });
      },
      j,
      k,
      i;

    // Deciding for which which all datasets we need which all sub-datasets
    for (i = 0; i < len; i++) {
      dataset = datasets[i];
      currSubDS = requiredSubDs[i] = {};
      currDerivedDataVal = derivedDataValue[i] = {
        showMean: {
          seriesname: 'Mean',
          valueposition: pluck(dataset.meanvalueposition, chartAttrs.meanvalueposition, ABOVE),
          showvalues: pluckNumber(dataset.showmeanvalues, chartAttrs.showmeanvalues, 0),
          anchorbgcolor: pluck(dataset.meaniconcolor, chartAttrs.meaniconcolor, COLOR_000000),
          anchorbordercolor: pluck(dataset.meaniconbordercolor, chartAttrs.meaniconbordercolor, COLOR_000000),
          anchorradius: pluckNumber(dataset.meaniconradius, chartAttrs.meaniconradius, 5),
          anchoralpha: pluck(dataset.alpha, dataset.meaniconalpha, chartAttrs.meaniconalpha),
          linethickness: pluck(dataset.linethickness, chartAttrs.linethickness, 1),
          anchorsides: pluck(dataset.meaniconsides, chartAttrs.meaniconsides, 3),
          showline: !pluckNumber(dataset.drawmeanconnector, chartAttrs.drawmeanconnector),
          dip: pluck(dataset.meaniconshape, chartAttrs.meaniconshape, POLYGON),
          color: pluck(dataset.meaniconcolor, chartAttrs.meaniconcolor, COLOR_000000),
          data: []
        },
        showSD: {
          seriesname: 'SD',
          valueposition: pluck(dataset.sdvalueposition, chartAttrs.sdvalueposition, ABOVE),
          showvalues: pluckNumber(dataset.showsdvalues, chartAttrs.showsdvalues, 0),
          anchorbgcolor: pluck(dataset.sdiconcolor, chartAttrs.sdiconcolor, COLOR_000000),
          anchorbordercolor: pluck(dataset.sdiconbordercolor, chartAttrs.sdiconbordercolor, COLOR_000000),
          anchorradius: pluckNumber(dataset.sdiconradius, chartAttrs.sdiconradius, 5),
          anchoralpha: pluck(dataset.alpha, dataset.sdiconalpha, chartAttrs.sdiconalpha),
          anchorbgalpha: pluck(dataset.sdiconalpha, chartAttrs.sdiconalpha, 100),
          linethickness: pluck(dataset.linethickness, chartAttrs.linethickness, 1),
          anchorsides: pluck(dataset.sdiconsides, chartAttrs.sdiconsides, 3),
          showline: !pluckNumber(dataset.drawsdconnector, chartAttrs.drawsdconnector),
          dip: pluck(dataset.sdiconshape, chartAttrs.sdiconshape, POLYGON),
          color: pluck(dataset.sdiconcolor, chartAttrs.sdiconcolor, COLOR_000000),
          data: []
        },
        showQD: {
          seriesname: 'QD',
          valueposition: pluck(dataset.qdvalueposition, chartAttrs.qdvalueposition, ABOVE),
          showvalues: pluckNumber(dataset.showqdvalues, chartAttrs.showqdvalues, 0),
          anchorbgcolor: pluck(dataset.qdiconcolor, chartAttrs.qdiconcolor, COLOR_000000),
          anchorbordercolor: pluck(dataset.qdiconbordercolor, chartAttrs.qdiconbordercolor, COLOR_000000),
          anchorradius: pluckNumber(dataset.qdiconradius, chartAttrs.qdiconradius, 5),
          anchoralpha: pluck(dataset.alpha, dataset.qdiconalpha, chartAttrs.qdiconalpha),
          anchorbgalpha: pluck(dataset.qdiconalpha, chartAttrs.qdiconalpha, 100),
          linethickness: pluck(dataset.linethickness, chartAttrs.linethickness, 1),
          anchorsides: pluck(dataset.qdiconsides, chartAttrs.qdiconsides, 3),
          showline: !pluckNumber(dataset.drawqdconnector, chartAttrs.drawqdconnector),
          dip: pluck(dataset.qdiconshape, chartAttrs.qdiconshape, POLYGON),
          color: pluck(dataset.qdiconcolor, chartAttrs.qdiconcolor, COLOR_000000),
          data: []
        },
        showMD: {
          seriesname: 'MD',
          valueposition: pluck(dataset.mdvalueposition, chartAttrs.mdvalueposition, ABOVE),
          showvalues: pluckNumber(dataset.showmdvalues, chartAttrs.showmdvalues, 0),
          anchorbgcolor: pluck(dataset.mdiconcolor, chartAttrs.mdiconcolor, COLOR_000000),
          anchorbordercolor: pluck(dataset.mdiconbordercolor, chartAttrs.mdiconbordercolor, COLOR_000000),
          anchorradius: pluckNumber(dataset.mdiconradius, chartAttrs.mdiconradius, 5),
          anchoralpha: pluck(dataset.alpha, dataset.mdiconalpha, chartAttrs.mdiconalpha),
          anchorbgalpha: pluck(dataset.mdiconalpha, chartAttrs.mdiconalpha, 100),
          linethickness: pluck(dataset.linethickness, chartAttrs.linethickness, 1),
          anchorsides: pluck(dataset.mdiconsides, chartAttrs.mdiconsides, 3),
          showline: !pluckNumber(dataset.drawmdconnector, chartAttrs.drawmdconnector),
          dip: pluck(dataset.mdiconshape, chartAttrs.mdiconshape, POLYGON),
          color: pluck(dataset.mdiconcolor, chartAttrs.mdiconcolor, COLOR_000000),
          data: []
        },
        outliers: {
          seriesname: 'Outliers',
          valueposition: pluck(dataset.outliervalueposition, chartAttrs.outliervalueposition, ABOVE),
          showvalues: pluckNumber(dataset.showoutliervalues, chartAttrs.showoutliervalues, 0),
          anchorbgcolor: pluck(dataset.outliericoncolor, chartAttrs.outliericoncolor, COLOR_000000),
          anchorbordercolor: pluck(dataset.outliericonbordercolor, chartAttrs.outliericonbordercolor, COLOR_000000),
          anchorradius: pluckNumber(dataset.outliericonradius, chartAttrs.outliericonradius, 5),
          anchoralpha: pluck(dataset.alpha, dataset.outliericonalpha, chartAttrs.outliericonalpha),
          includeinlegend: dataset.seriesname ? 1 : 0,
          anchorbgalpha: pluck(dataset.outliericonalpha, chartAttrs.outliericonalpha, 100),
          linethickness: pluck(dataset.linethickness, chartAttrs.linethickness, 1),
          anchorsides: pluck(dataset.outliericonsides, chartAttrs.outliericonsides, 3),
          showline: !pluckNumber(dataset.drawoutliersconnector, chartAttrs.drawoutliersconnector),
          color: pluck(dataset.outliericoncolor, chartAttrs.outliericoncolor, COLOR_000000),
          dip: pluck(dataset.outliericonshape, chartAttrs.outliericonshape, POLYGON),
          data: []
        }
      };
      showMean = pluckNumber(dataset.showmean, chartAttrs.showmean, 0);
      showSD = pluckNumber(dataset.showsd, chartAttrs.showsd, 0);
      showQD = pluckNumber(dataset.showqd, chartAttrs.showqd, 0);
      showMD = pluckNumber(dataset.showmd, chartAttrs.showmd, 0);

      data = dataset.data;
      dataLen = (data && data.length) || 0;
      for (j = 0; j < dataLen; j++) {
        setData = data[j];
        if (setData.min && setData.max && setData.q1 && setData.q3 && setData.median) {
          meanValue = setData.mean;
          sdValue = setData.sd;
          mdValue = setData.md;
          qdValue = setData.qd;
        } else {
          bwCalc.setArray(setData.value);
          bwCalc.getQuartiles();
          meanValue = bwCalc.getMean();
          sdValue = bwCalc.getSD();
          qdValue = bwCalc.getQD();
          mdValue = bwCalc.getMD();
        }
        outliers = setData.outliers;

        // Checking if at all mean, sd, etc datasets are required
        showMean = currSubDS.showMean = currSubDS.showMean || pluckNumber(setData.showmean, showMean);
        showSD = currSubDS.showSD = currSubDS.showSD || pluckNumber(setData.showsd, showSD);
        showQD = currSubDS.showQD = currSubDS.showQD || pluckNumber(setData.showqd, showQD);
        showMD = currSubDS.showMD = currSubDS.showMD || pluckNumber(setData.showmd, showMD);

        // Storing the value of mean, sd as the same key of the previous check for ease of use
        currDerivedDataVal.showMean.data.push({
          showvalue: setData.showmeanvalue,
          valueposition: setData.meanvalueposition,
          value: showMean && (meanValue || UNDEF),
          anchorbgcolor: setData.meaniconcolor,
          anchorbordercolor: setData.meaniconbordercolor,
          anchorradius: setData.meaniconradius,
          anchoralpha: setData.meaniconalpha,
          anchorbgalpha: setData.meaniconalpha,
          anchorsides: setData.meaniconsides,
          dip: setData.meaniconshape
        });
        currDerivedDataVal.showSD.data.push({
          showvalue: setData.showsdvalue,
          valueposition: setData.sdvalueposition,
          value: showSD && (sdValue || UNDEF),
          anchorbgcolor: setData.sdiconcolor,
          anchorbordercolor: setData.sdiconbordercolor,
          anchorradius: setData.sdiconradius,
          anchoralpha: setData.sdiconalpha,
          anchorbgalpha: setData.sdiconalpha,
          anchorsides: setData.sdiconsides,
          dip: setData.sdiconshape
        });
        currDerivedDataVal.showQD.data.push({
          showvalue: setData.showqdvalue,
          valueposition: setData.qdvalueposition,
          value: showQD && (qdValue || UNDEF),
          anchorbgcolor: setData.qdiconcolor,
          anchorbordercolor: setData.qdiconbordercolor,
          anchorradius: setData.qdiconradius,
          anchoralpha: setData.qdiconalpha,
          anchorbgalpha: setData.qdiconalpha,
          anchorsides: setData.qdiconsides,
          dip: setData.qdiconshape
        });
        currDerivedDataVal.showMD.data.push({
          showvalue: setData.showmdvalue,
          valueposition: setData.mdvalueposition,
          value: showMD && (mdValue || UNDEF),
          anchorbgcolor: setData.mdiconcolor,
          anchorbordercolor: setData.mdiconbordercolor,
          anchorradius: setData.mdiconradius,
          anchoralpha: setData.mdiconalpha,
          anchorbgalpha: setData.mdiconalpha,
          anchorsides: setData.mdiconsides,
          dip: setData.mdiconshape
        });

        if (outliers) {
          outliersArr = currDerivedDataVal.outliers.data;
          outliers.split(COMMA).forEach(fn);
          currSubDS.outliers = true;
        }
      }
    }

    // Creating the subDataset as per their requirements
    subDSLen = requiredSubDs.length; // eslint-disable-line good-practices/no-single-usage-variable
    for (k = 0; k < subDSLen; k++) {
      currSubDS = requiredSubDs[k];
      currDerivedDS = derivedDS[k] = [];
      for (i in currSubDS) {
        currSubDS[i] && currDerivedDS.push(derivedDataValue[k][i]);
      }
    }

    return derivedDS;
  }

  /**
   * This method return the dataset definations for this charts
   * @return {Object} return column dataset
   */
  getDSdef () {
    return BoxAndWhiskerDataset;
  }

  /**
   * This method return the dataset-group definations for this charts
   * @return {Object} Multiseries column group definition
   */
  getDSGroupdef () {
    return BoxAndWhiskerMultiSeriesGroup;
  }
}
export default BoxAndWhisker2D;
